import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api/client.ts';
import { PrimaryButton } from '../components/ui/PrimaryButton.tsx';
import { TextField } from '../components/ui/TextField.tsx';
import { formatDuration, formatSize, todayIso } from '../lib/format.ts';
import { usePendingUpload } from '../lib/usePendingUpload.ts';
import './UploadScreen.css';

export function UploadScreen() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const transfer = usePendingUpload();

  const [title, setTitle] = useState('');
  const [tripDate, setTripDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function handleFileChosen(chosen: File | undefined) {
    if (!chosen) return;
    setSaveError(null);
    // A filename is a better starting point than an empty field, but never
    // overwrites something already typed.
    if (!title) setTitle(chosen.name.replace(/\.[^.]+$/, ''));
    void transfer.pick(chosen);
  }

  async function handleSubmit() {
    if (!transfer.uploadId || !canSubmit) return;
    setSaveError(null);
    setSaving(true);
    try {
      await api.saveVideo({
        uploadId: transfer.uploadId,
        title: title.trim(),
        tripDate,
        durationSec: transfer.durationSec,
      });
      navigate('/', { replace: true });
    } catch (err) {
      setSaving(false);
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить видео');
    }
  }

  // Enabled only once the bytes are on the server and both fields are filled.
  const canSubmit =
    transfer.phase === 'uploaded' && !saving && title.trim().length > 0 && tripDate.length > 0;
  const error = saveError ?? transfer.error;

  return (
    <div className="screen upload-screen">
      <h1 className="upload-title">Загрузка видео</h1>

      {/* Tap opens the OS picker — no drag-and-drop, this is mobile. */}
      <button
        type="button"
        className="dropzone"
        onClick={() => fileInputRef.current?.click()}
        disabled={transfer.busy || saving}
      >
        <Upload size={26} strokeWidth={1.75} />
        <span className="dropzone__label">{transfer.file ? transfer.file.name : 'Выбрать видео'}</span>
        {transfer.file && (
          <span className="dropzone__meta">
            {formatSize(transfer.file.size)}
            {transfer.durationSec > 0 && ` · ${formatDuration(transfer.durationSec)}`}
          </span>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => handleFileChosen(e.target.files?.[0])}
      />

      {transfer.phase !== 'idle' && transfer.phase !== 'probing' && (
        <div className="upload-progress">
          <div className="upload-progress__track">
            <div className="upload-progress__fill" style={{ width: `${transfer.progress}%` }} />
          </div>
          <div className="upload-progress__label">
            {transfer.phase === 'uploaded' ? 'Загружено · 100%' : `${transfer.progress}%`}
          </div>
        </div>
      )}

      {transfer.phase === 'probing' && <div className="muted-note upload-status">Читаем видео…</div>}
      {error && <div className="form-error upload-status">{error}</div>}

      <div className="upload-fields">
        <TextField
          placeholder="Название"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
        />
        <TextField
          type="date"
          placeholder="Дата поездки"
          value={tripDate}
          onChange={(e) => setTripDate(e.target.value)}
          disabled={saving}
        />
        <PrimaryButton className="upload-submit" onClick={handleSubmit} disabled={!canSubmit}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </PrimaryButton>
      </div>
    </div>
  );
}
